/**
 * Cognito sign-up / sign-in over SRP, plus Google sign-in over Hosted
 * UI's OAuth2 authorization-code flow. Cloned from tengrade's
 * web-examiner/src/auth/cognito.ts (ACTION_PLAN.md E4); the Google leg
 * is ported from tengrade's R7 design §4 (Google-only — no Apple, no
 * per-org SSO lookup, neither of which this product needs).
 *
 * `amazon-cognito-identity-js` rather than Amplify: the pool client is
 * SRP-only with no client secret, and this is the whole of the auth
 * surface — Amplify would be ~10x the bundle for the same calls.
 *
 * The portal sends the **ID token** on API calls, deliberately: the HTTP
 * API authoriser is configured with `jwt_audience = [client_id]`, which
 * only ID tokens carry, and generate/handler.py reads the organisation
 * from the verified `email` claim, which access tokens do not carry.
 */

import {
  AuthenticationDetails,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

import { config } from '../config';

export interface User {
  email: string;
  /** The email's domain. Nothing in the portal reads this. It is NOT the API's
   *  organisation for a free-mail address: api_common.py's `org_from_claims`
   *  returns the whole address there, and the portal does not mirror that list. */
  org: string;
}

export class AuthError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/** Raised when sign-in needs a TOTP code (the pool allows optional MFA,
 *  auth_stack.py's `mfa_second_factor=cognito.MfaSecondFactor(otp=True)`). */
export class TotpRequired extends Error {
  constructor(readonly submit: (code: string) => Promise<CognitoUserSession>) {
    super('A one-time code from your authenticator app is required.');
    this.name = 'TotpRequired';
  }
}

let pool: CognitoUserPool | null = null;

function userPool(): CognitoUserPool {
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.clientId,
    });
  }
  return pool;
}

/**
 * The pre-sign-up domain-vetting trigger raises a plain Exception; Cognito
 * wraps it as "PreSignUp failed with error <message>." Show the caller the
 * vetting reason, not the plumbing.
 */
function stripLambdaWrapper(message: string): string {
  const lambdaFailure = /^PreSignUp failed with error (.*)$/.exec(message);
  const inner = lambdaFailure?.[1] ?? message;
  // Cognito appends its own trailing period onto the Lambda's raw
  // exception text — which, for a message that already ends in one of
  // its own (as domain_vetting/handler.py's do), leaves two. Collapse
  // any trailing run down to exactly one rather than stripping a fixed
  // count, so this is right regardless of how the source message ends.
  return inner.replace(/\.+$/, '.');
}

function readableError(err: unknown): AuthError {
  const raw = err as { message?: string; code?: string; name?: string };
  let message = stripLambdaWrapper(raw?.message ?? 'Something went wrong. Please try again.');
  if (message === 'User does not exist.' || message === 'Incorrect username or password.') {
    message = 'That email address and password combination was not recognised.';
  }
  return new AuthError(message, raw?.code ?? raw?.name);
}

export function signUp(email: string, password: string): Promise<{ confirmed: boolean }> {
  return new Promise((resolve, reject) => {
    userPool().signUp(
      email,
      password,
      [new CognitoUserAttribute({ Name: 'email', Value: email })],
      [],
      (err, result) => {
        if (err) return reject(readableError(err));
        resolve({ confirmed: Boolean(result?.userConfirmed) });
      },
    );
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: email, Pool: userPool() }).confirmRegistration(
      code,
      true,
      (err) => (err ? reject(readableError(err)) : resolve()),
    );
  });
}

export function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: email, Pool: userPool() }).resendConfirmationCode((err) =>
      err ? reject(readableError(err)) : resolve(),
    );
  });
}

export function signIn(email: string, password: string): Promise<CognitoUserSession> {
  const user = new CognitoUser({ Username: email, Pool: userPool() });
  return new Promise((resolve, reject) => {
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(readableError(err)),
        totpRequired: () => {
          reject(
            new TotpRequired(
              (code) =>
                new Promise<CognitoUserSession>((res, rej) => {
                  user.sendMFACode(
                    code,
                    {
                      onSuccess: (session) => res(session),
                      onFailure: (err) => rej(readableError(err)),
                    },
                    'SOFTWARE_TOKEN_MFA',
                  );
                }),
            ),
          );
        },
        newPasswordRequired: () => {
          reject(
            new AuthError(
              'This account must set a new password before signing in. ' +
                'Use "Forgot password" or ask your organisation owner to reset it.',
              'NewPasswordRequired',
            ),
          );
        },
      },
    );
  });
}

export function signOut(): void {
  userPool().getCurrentUser()?.signOut();
}

/**
 * Current ID token, refreshed if it has expired.
 *
 * `getSession` transparently exchanges the refresh token when the ID
 * token is stale, so every API call asks for the token rather than
 * caching one for the session's lifetime.
 */
export function currentIdToken(): Promise<string | null> {
  const user = userPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export function userFromSession(session: CognitoUserSession): User {
  const claims = session.getIdToken().payload as Record<string, unknown>;
  const email = String(claims.email ?? '');
  return {
    email,
    org: String(claims['custom:org'] ?? email.split('@')[1] ?? ''),
  };
}

/** Restore a signed-in user on page load, if the refresh token is alive. */
export function currentUser(): Promise<User | null> {
  const user = userPool().getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(userFromSession(session));
    });
  });
}

// ── Google sign-in (ported from tengrade's R3/R7 design) ──────────────

/**
 * Hosted UI's callback path — the exact string baked into the pool
 * client's `callback_urls` (auth_stack.py) as `{portal_base_url}/sso/
 * callback`, so this must match verbatim or Cognito rejects the
 * authorize request with `redirect_mismatch`.
 */
export function ssoRedirectUri(): string {
  return `${window.location.origin}/sso/callback`;
}

export type SsoCallbackParams = { code: string } | { error: string } | null;

/**
 * Hosted UI redirects back to /sso/callback with either `?code=...` or
 * `?error=...&error_description=...` when the PreSignUp trigger rejects
 * the sign-in outright (domain_vetting/handler.py's
 * `_handle_external_provider`: no matching verified fibtec-etlgen
 * account for the Google email, most commonly). The error case never
 * reaches exchangeSsoCode's own token-exchange call below — nothing to
 * redeem — so SignIn.tsx needs both branches from this one parse, not
 * just `code`.
 */
export function ssoCallbackParams(): SsoCallbackParams {
  if (window.location.pathname !== '/sso/callback') return null;
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (code) return { code };
  const description = params.get('error_description');
  return description ? { error: stripLambdaWrapper(description) } : null;
}

/** The Hosted UI URL the "Continue with Google" button redirects to. */
export function ssoAuthorizeUrl(providerName: string): string {
  const params = new URLSearchParams({
    identity_provider: providerName,
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email',
    redirect_uri: ssoRedirectUri(),
  });
  return `${config.hostedUiUrl}/oauth2/authorize?${params.toString()}`;
}

/**
 * Exchanges the `code` Hosted UI's redirect carries for tokens, then
 * bridges them into `amazon-cognito-identity-js`'s own session storage.
 *
 * The OAuth2 authorization-code flow (Hosted UI) and SRP (`signIn`
 * above) are different protocols returning different shapes — a plain
 * JSON `{id_token, access_token, refresh_token}` here, not a
 * `CognitoUserSession`. `CognitoUser.setSignInUserSession` is the
 * library's own public, documented bridge (confirmed against the
 * installed amazon-cognito-identity-js@^6.3.12 types, same version
 * tengrade's own R3 design verified this against): it both sets the
 * in-memory session and caches it under the library's own storage keys,
 * so `currentIdToken()`/`currentUser()` above need no SSO-specific
 * branch — they find a valid session afterward exactly as they would
 * after SRP sign-in.
 */
export async function exchangeSsoCode(code: string): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${config.hostedUiUrl}/oauth2/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: config.clientId,
        code,
        redirect_uri: ssoRedirectUri(),
      }).toString(),
    });
  } catch {
    throw new AuthError('Could not reach the sign-in server. Please try again.');
  }
  if (!response.ok) {
    throw new AuthError('Sign-in with Google failed. Please try again.');
  }

  const tokens = (await response.json()) as {
    id_token: string;
    access_token: string;
    refresh_token: string;
  };
  const idToken = new CognitoIdToken({ IdToken: tokens.id_token });
  const session = new CognitoUserSession({
    IdToken: idToken,
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.access_token }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refresh_token }),
  });
  const username = String(idToken.payload['cognito:username'] ?? idToken.payload.sub ?? '');
  new CognitoUser({ Username: username, Pool: userPool() }).setSignInUserSession(session);
}
